import DimensionUtils from "../common/DimensionUtils";
import { IResolvedUnitConfig } from "../common/inputs/unitConfig";
import { ISizeParamTypeDescriptor } from "../database/interfaces/paramScript/IParamTypeDescriptor";
import TParameters from "./TParameters";

/**
 * KERESZT-MEZŐ KORLÁTOK — egy méret-mező min/max korlátja EGY MÁSIK paraméter aktuális értékéből.
 *
 * MIÉRT: vannak szabályok, amiket egyetlen mező descriptorába nem lehet beírni, mert két mező VISZONYÁRÓL
 * szólnak. Konkrét eset: a munkalap-összeállításoknál a hosszúság mindig legyen nagyobb a szélességnél.
 * Ezt a descriptor `minFrom` / `maxFrom` mezője mondja ki, ugyanabban az alakban, ahogy a `visibleWhen`
 * hivatkozik másik paraméterre ({paramName, property, field?}) — így nincs új fogalom a nyelvben.
 *
 * HOGYAN: a feloldott korlát a NumericInput `min` / `max` propjába megy, tehát a betartatást a MÁR
 * MEGLÉVŐ clamp végzi (léptetés, gépelés utáni blur, Enter). Nincs új validációs ág, nincs külön
 * hibaüzenet, és nem "ugrik el" a másik mező a felhasználó keze alól.
 *
 * ENGEDÉKENY, ha nem tud dönteni: ha a hivatkozott érték hiányzik, nem szám, vagy MÁS EGYSÉGBEN van
 * (pl. a másik mező épp "auto"), akkor NINCS korlát. Inkább ne korlátozzunk, mint hogy két
 * összehasonlíthatatlan értékből kitalált határt erőltessünk a mezőre.
 *
 * AMIT NEM FED LE: a betöltési guard (paramGuards) nem látja a többi paramétert, csak a sajátját —
 * egy régebben mentett, szabálysértő kombinációt tehát betöltéskor nem javít ki, csak a következő
 * szerkesztéstől kezdve tartja a szabályt.
 */

/** Hivatkozás egy másik paraméter mezőjére — a descriptorból (lásd ISizeParamTypeDescriptor). */
export type TParamRef = NonNullable<ISizeParamTypeDescriptor["minFrom"]>;

/**
 * Egy hivatkozott méret-mező AKTUÁLIS értéke számként, CSAK ha ugyanabban az egységben van, mint a
 * korlátozandó mező. A keresés ugyanaz, mint a paramVisibility-nél: `parameters[paramName][property]`,
 * composite értéknél a `field` kulccsal.
 */
export const resolveDimensionRef = (
    parameters: TParameters,
    ref: TParamRef | undefined,
    unit: string
): number | undefined => {
    if (!ref) return undefined;

    const raw = parameters?.[ref.paramName]?.[ref.property];
    const value = ref.field && raw && typeof raw === "object" ? raw[ref.field] : raw;
    const parsed = DimensionUtils.parseDimension(String(value ?? ""));

    return (isNaN(parsed.num) || parsed.unit !== unit) ? undefined : parsed.num;
};

/**
 * A feloldott unit-config SZŰKÍTÉSE a kereszt-mező korlátokkal.
 *
 * Szándékosan ugyanazt a `IResolvedUnitConfig` alakot adja vissza, amit kapott: a méret-mező numerikus
 * konfigurációja így EGYETLEN objektum marad (a kártya továbbra is csak `cfg`-t olvas), nem hasad
 * szét "unit-config + külön határok" párosra.
 *
 * A descriptor saját korlátai mindig ERŐSEBBEK — a kereszt-korlát csak szűkíthet, tágítani nem tud. Ha
 * a két szabály ellentmondana (az alsó határ a felső fölé kerülne), a mező a felső határra zárul: így
 * soha nem áll elő min > max, amit a NumericInput clampje nem tudna értelmezni.
 *
 * Ha egyik hivatkozás sem old fel értéket, az EREDETI objektumot adja vissza (nincs felesleges új
 * referencia, tehát a memoizált hívók sem számolnak újra hiába).
 */
export const withCrossFieldBounds = (
    cfg: IResolvedUnitConfig,
    parameters: TParameters,
    // Szándékosan STRUKTURÁLIS a típus (csak a két hivatkozás kell belőle), nem `ISizeParamTypeDescriptor`:
    // így egy composite AL-MEZŐ leírója is átadható (lásd TCompositeFieldDescriptor "size" ága), és a
    // kereszt-korlát nem csak az önálló Size paramétereknél működik.
    descriptor: { minFrom?: TParamRef; maxFrom?: TParamRef } | undefined
): IResolvedUnitConfig => {
    const lower = resolveDimensionRef(parameters, descriptor?.minFrom, cfg.unit);
    const upper = resolveDimensionRef(parameters, descriptor?.maxFrom, cfg.unit);
    if (lower === undefined && upper === undefined) return cfg;

    const max = Math.min(cfg.max, upper ?? cfg.max);
    const min = Math.min(Math.max(cfg.min, lower ?? cfg.min), max);

    return { ...cfg, min, max };
};

/**
 * KERET-KORLÁT — a fix fogyasztók összege nem lépheti túl a befoglaló méretet.
 *
 * MIÉRT KELL A "legalább egy arányos mező" SZABÁLY MELLÉ: az csak azt akadályozza meg, hogy MINDEN
 * mező fix legyen. Azt nem, hogy a fix mezők ÖSSZEGE túllépje a keretet — egy 1200 mm-es szekrényben
 * két 900 mm-es zóna külön-külön érvényes, együtt viszont 1800 mm-t követelne, és szétvágná a
 * szekrényt. Ugyanez egy zónán belül a gyerekek között, és a `%`-oknál is (három 50% = 150%).
 *
 * HÁROMFÉLE FOGYASZTÓ:
 *   `mm`  — abszolút mennyiséget vesz el a keretből,
 *   `%`   — a keret ismert hányadát,
 *   `*`   — SÚLY: azt kapja, ami marad, tehát a keretből semmit nem foglal le előre.
 *
 * A visszaadott érték a mező FELSŐ HATÁRA a SAJÁT egységében; a betartatást a NumericInput meglévő
 * clampje végzi (nincs új validációs ág, és nem ugrik el a másik mező a felhasználó keze alól).
 * `undefined`, ha nincs mit korlátozni: súly-egységű mezőnél, feloldhatatlan keretnél, vagy ha maga a
 * keret sem fix (pl. a zóna magassága `1*` — ilyenkor a zóna abszolút mérete nem ismert).
 */
export type TBudgetRef = { from: TParamRef; siblings?: TParamRef[] };

/** Egy "600mm" / "33%" / "1*" alakú érték abszolút fogyasztása a keretből. */
const consumption = (valueStr: string, total: number): number => {
    const parsed = DimensionUtils.parseDimension(String(valueStr ?? ""));
    if (isNaN(parsed.num)) return 0;
    if (parsed.unit === "mm") return parsed.num;
    if (parsed.unit === "%") return (total * parsed.num) / 100;
    return 0; // súly (`*`): nem foglal le előre semmit
};

/** A keret abszolút mérete mm-ben, vagy `undefined`, ha nem feloldható / nem fix. */
export const resolveBudgetTotal = (parameters: TParameters, ref: TParamRef | undefined): number | undefined => {
    if (!ref) return undefined;

    const raw = parameters?.[ref.paramName]?.[ref.property];
    const value = ref.field && raw && typeof raw === "object" ? raw[ref.field] : raw;
    const parsed = DimensionUtils.parseDimension(String(value ?? ""));

    return (isNaN(parsed.num) || parsed.unit !== "mm") ? undefined : parsed.num;
};

/**
 * A mező felső határa a keretből, a TÖBBI fogyasztó levonása után. Az `others` a testvérek tárolási
 * alakú értékei — a sajátja NÉLKÜL.
 */
export const budgetMax = (
    parameters: TParameters,
    budget: TBudgetRef | undefined,
    unit: string,
    others: string[]
): number | undefined => {
    if (!budget || (unit !== "mm" && unit !== "%")) return undefined;

    const total = resolveBudgetTotal(parameters, budget.from);
    if (total === undefined || total <= 0) return undefined;

    const free = Math.max(0, total - others.reduce((sum, value) => sum + consumption(value, total), 0));

    return unit === "mm" ? free : (free / total) * 100;
};

/** A `siblings`-ben felsorolt paraméterek jelenlegi értékei — a keret-korlát `others` bemenete. */
export const siblingValues = (parameters: TParameters, budget: TBudgetRef | undefined): string[] =>
    (budget?.siblings ?? []).map((ref) => {
        const raw = parameters?.[ref.paramName]?.[ref.property];
        const value = ref.field && raw && typeof raw === "object" ? raw[ref.field] : raw;
        return String(value ?? "");
    });


export type TBoardRef = NonNullable<ISizeParamTypeDescriptor["maxFromBoard"]>;

export const boardBoundMax = (
    parameters: TParameters,
    boards: { findByID: (id: string) => { [key: string]: any } | null } | null | undefined,
    ref: TBoardRef | undefined,
    unit: string
): number | undefined => {
    if (!ref || !boards || unit !== "mm") return undefined;

    const decor = parameters?.[ref.paramName]?.[ref.property];
    const boardId = decor && typeof decor === "object" ? decor[ref.field] : undefined;
    if (typeof boardId !== "string" || !boardId) return undefined;

    const size = boards.findByID(boardId)?.[ref.attribute];
    return typeof size === "number" && size > 0 ? size : undefined;
};
